import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from './pilot-ui/dialog';
import Button from './ui/Button';
import LabelPhotoImage, { rotationDegrees, type LabelPhotoImageProps } from './LabelPhotoImage';
import LabelPhotoZoom from './LabelPhotoZoom';
import { announceTurn } from '../utils/labelPhotoWindow';

/**
 * FS-1079 — a label photo opened large inside the console, turned the way its
 * thumbnail is.
 *
 * The turn is record data and the file is never changed, so a browser tab
 * opened on the file shows it unturned: a sideways label stays sideways. Every
 * place that draws a label photo opens it here instead, where it can be
 * zoomed, moved around and popped out to its own window (LabelPhotoZoom), and
 * the stored file stays one link away.
 */

/** The thumbnail's hover help. */
export const OPEN_LARGE_TITLE = 'Show this photo large, turned the way it is drawn here';

/** The viewer's file link, by whether the photo shown is a converted copy of the upload. */
export const fileLinkTitle = (converted: boolean): string =>
  converted
    ? 'Open the converted copy of this photo in a new tab, as stored and not turned, to zoom in or download it'
    : 'Open this photo in a new tab, as it was uploaded and not turned, to zoom in or download it';

interface LabelPhotoOpenerProps
  extends Pick<LabelPhotoImageProps, 'src' | 'alt' | 'degrees' | 'boxClassName' | 'fit' | 'onError'> {
  /** Extra look for the thumbnail only (a faded removed photo); the viewer draws the photo at full strength to be read. */
  imageClassName?: string;
  /** The thumbnail button's accessible name. */
  label: string;
  /** The viewer's heading. */
  heading: string;
  /** Hover help for the viewer's link to the file (`fileLinkTitle` unless the place says more). */
  fileTitle: string;
  /** The Rotate this place offers beside the thumbnail, offered in the viewer too; absent where there is none. */
  rotate?: React.ReactNode;
  /** Layout for the thumbnail button within its place. */
  className?: string;
}

/** A label photo thumbnail that opens the photo large in the console. */
export const LabelPhotoOpener: React.FC<LabelPhotoOpenerProps> = ({
  src,
  alt,
  degrees,
  boxClassName,
  fit,
  imageClassName,
  onError,
  label,
  heading,
  fileTitle,
  rotate,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const thumbnail = useRef<HTMLButtonElement>(null);
  // A Rotate here also turns the photo in a window it was popped out to.
  const turn = rotationDegrees(degrees);
  const announced = useRef(turn);
  useEffect(() => {
    if (announced.current === turn) return;
    announced.current = turn;
    announceTurn(src, turn);
  }, [src, turn]);
  return (
    <>
      <Button
        ref={thumbnail}
        variant="icon"
        onClick={() => setOpen(true)}
        aria-label={label}
        title={OPEN_LARGE_TITLE}
        // A block-level box, as the link it replaces drew: an inline button
        // would sit on the text baseline and leave a gap under the photo.
        className={`flex h-auto p-0 ${className}`}
      >
        <LabelPhotoImage
          src={src}
          alt={alt}
          degrees={degrees}
          boxClassName={boxClassName}
          fit={fit}
          imageClassName={imageClassName}
          onError={onError}
        />
      </Button>
      <LabelPhotoViewer
        open={open}
        onClose={() => setOpen(false)}
        returnFocusTo={thumbnail}
        src={src}
        alt={alt}
        degrees={degrees}
        heading={heading}
        fileTitle={fileTitle}
        rotate={rotate}
      />
    </>
  );
};

interface LabelPhotoViewerProps {
  open: boolean;
  onClose: () => void;
  /** Where focus goes when the viewer closes: the thumbnail that opened it. */
  returnFocusTo: React.RefObject<HTMLButtonElement | null>;
  src: string;
  alt: string;
  degrees: LabelPhotoImageProps['degrees'];
  heading: string;
  fileTitle: string;
  rotate?: React.ReactNode;
}

/**
 * React passes an event from a portal up the component tree, not the page, so
 * a press in the viewer would also reach the drawer, dialog or card that holds
 * the thumbnail and could close or reset it. The viewer keeps its events.
 */
const keepInside = (event: React.SyntheticEvent) => event.stopPropagation();

const LabelPhotoViewer: React.FC<LabelPhotoViewerProps> = ({
  open,
  onClose,
  returnFocusTo,
  src,
  alt,
  degrees,
  heading,
  fileTitle,
  rotate,
}) => (
  <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
    {open && (
      // `contents`: no box of its own, so the place's layout is unchanged.
      <div className="contents" onClick={keepInside} onPointerDown={keepInside}>
        <DialogContent
          customWidth
          forceBackdrop
          data-testid="label-photo-viewer"
          finalFocus={returnFocusTo}
          // The window less the shared dialog's 1rem gutter, both ways, so the
          // photo is as large as the window allows.
          className="flex h-[calc(100%-2rem)] w-full flex-col gap-0 overflow-hidden p-0"
        >
          <div className="shrink-0 border-b p-4 pr-12">
            <DialogTitle className="text-lg font-semibold">{heading}</DialogTitle>
            <DialogDescription className="text-xs">
              Shown turned the way the console draws it. The photo file itself is not changed.
            </DialogDescription>
          </div>
          <LabelPhotoZoom src={src} alt={alt} degrees={degrees} fileTitle={fileTitle} rotate={rotate} popOut />
        </DialogContent>
      </div>
    )}
  </Dialog>
);
